import type { JSX } from 'react';

import type {
  EmployeeDetails,
  EmployeeOptions,
  IsoDate,
  WeekCapacity,
  WeekCapacityStatus,
} from '@asas/domain';

import type { FehlerBefund } from '../daten/fehler';

import {
  BESCHAEFTIGUNG_LABELS,
  beschaeftigungAm,
  datumAnzeige,
  istAenderbar,
  istEntfernbar,
  istZuVervollstaendigen,
  naechsterFachgruppenwechsel,
  sollHeute,
  stundenAnzeige,
  typAnzeige,
} from './formular';
import type { FormularFeld, FormularStand } from './formular';

/**
 * Die Ansichten der Mitarbeiterverwaltung (AC-103).
 *
 * Reine Ansichten wie `SessionPanel` und `TrpcPanel`: Daten und Rueckrufe
 * hinein, Markup hinaus. Damit genuegt der vorhandene Node-Renderer als Test,
 * ohne DOM-Aufbau, und Zustand, Netz und Fehlerbehandlung liegen an EINER
 * Stelle (`MitarbeiterBereich`).
 *
 * Bewusst schmucklos — keine Styling-Bibliothek, kein Router. Die Portierung
 * des Mockups (E2 ff.) ersetzt die Darstellung; die Daten-Service-Schicht und
 * die Formularlogik in `formular.ts` bleiben.
 */

function namenVon(eintraege: readonly { id: string; name: string }[]): Map<string, string> {
  return new Map(eintraege.map((eintrag) => [eintrag.id, eintrag.name]));
}

/** Der Name zu einer Kennung — `—` ohne Kennung (extern, zu vervollstaendigen) oder ohne Treffer. */
function nameZu(namen: ReadonlyMap<string, string>, id: string | null | undefined): string {
  return id === null || id === undefined ? '—' : (namen.get(id) ?? '—');
}

/**
 * Der Status in der Liste. Wer nicht planbar ist, hat keine Beschaeftigung im
 * Sinne der Kapazitaet; dort zaehlt, was noch fehlt (AC-103, Ergaenzung).
 */
function statusAnzeige(employee: EmployeeDetails, heute: IsoDate): string {
  if (employee.recordStatus === 'zu_vervollstaendigen') return 'Zu vervollständigen';
  if (employee.personType === 'extern') return 'Extern – ohne Kapazität';

  return BESCHAEFTIGUNG_LABELS[beschaeftigungAm(employee, heute)];
}

export interface KopfzeileProps {
  /** Sind Liste und Auswahl geladen? Vorher gibt es nur „oeffnen". */
  readonly geladen: boolean;
  readonly laedt: boolean;
  readonly sendet: boolean;
  /** Der Befund des LETZTEN Ladeversuchs — auch dann, wenn schon eine Liste dasteht. */
  readonly ladefehler: FehlerBefund | null;
  readonly anmeldeUrl: string;
  /** Der Weg zur Neuanmeldung steht schon weiter unten — mit gesicherten Eingaben; hier nicht doppelt. */
  readonly anmeldungAngeboten: boolean;
  readonly onLaden: () => void;
  readonly onNeu: () => void;
}

/**
 * Kopf der Mitarbeiterverwaltung: Laden, Neu laden, Anlegen — und der Befund
 * eines gescheiterten Ladeversuchs.
 *
 * Ein nicht wiederholbarer Befund (403) SPERRT das Laden: jeder weitere Klick
 * schriebe je Prozedur eine unloeschbare Audit-Zeile (AC-007; AC-016,
 * Entscheidung 3). Angeboten wird dann die Neuanmeldung, sofern der Befund sie
 * vorsieht. Waehrend geladen oder gespeichert wird, ist auch „Mitarbeiter
 * anlegen" gesperrt.
 */
export function Kopfzeile({
  geladen,
  laedt,
  sendet,
  ladefehler,
  anmeldeUrl,
  anmeldungAngeboten,
  onLaden,
  onNeu,
}: KopfzeileProps): JSX.Element {
  const ladenGesperrt = laedt || sendet || (ladefehler !== null && !ladefehler.wiederholbar);

  return (
    <>
      {ladefehler === null ? null : <p role="alert">{ladefehler.meldung}</p>}
      {ladefehler?.neuAnmelden === true && !anmeldungAngeboten ? (
        <p>
          <a href={anmeldeUrl}>Neu anmelden</a>
        </p>
      ) : null}
      <p>
        {geladen ? (
          <>
            <button type="button" disabled={sendet || laedt} onClick={onNeu}>
              Mitarbeiter anlegen
            </button>{' '}
          </>
        ) : null}
        <button type="button" disabled={ladenGesperrt} onClick={onLaden}>
          {laedt ? 'Wird geladen …' : geladen ? 'Neu laden' : 'Mitarbeiterverwaltung öffnen'}
        </button>
      </p>
    </>
  );
}

/** „ab 02.11.2026: Elektroplanung" fuer einen geplanten Wechsel — oder nichts. */
function wechselHinweis(
  wechsel: { readonly effectiveFrom: IsoDate; readonly fachgruppeId: string } | null,
  fachgruppen: ReadonlyMap<string, string>,
): JSX.Element | null {
  if (wechsel === null) return null;

  return (
    <small>
      {' '}
      (ab {datumAnzeige(wechsel.effectiveFrom)}: {fachgruppen.get(wechsel.fachgruppeId) ?? '—'})
    </small>
  );
}

export interface MitarbeiterListeProps {
  readonly mitarbeiter: readonly EmployeeDetails[];
  readonly optionen: EmployeeOptions;
  /** Heute, nur fuer die Anzeige des Status. */
  readonly heute: IsoDate;
  readonly ausgewaehlt: string | null;
  /**
   * Waehrend gespeichert oder geladen wird, wechselt die Auswahl nicht — sonst
   * landete die Antwort beim falschen Formular.
   */
  readonly gesperrt: boolean;
  readonly onAuswahl: (id: string) => void;
}

export function MitarbeiterListe({
  mitarbeiter,
  optionen,
  heute,
  ausgewaehlt,
  gesperrt,
  onAuswahl,
}: MitarbeiterListeProps): JSX.Element {
  if (mitarbeiter.length === 0) {
    return <p>Noch keine Mitarbeiter erfasst.</p>;
  }

  const gesellschaften = namenVon(optionen.companies);
  const fachgruppen = namenVon(optionen.fachgruppen);
  const rollen = namenVon(optionen.roles);
  const offen = mitarbeiter.filter(
    (employee) => employee.recordStatus === 'zu_vervollstaendigen',
  ).length;

  return (
    <>
      {offen === 0 ? null : (
        <p>
          {offen === 1
            ? 'Ein Stammsatz aus PACS ist noch zu vervollständigen.'
            : `${String(offen)} Stammsätze aus PACS sind noch zu vervollständigen.`}{' '}
          <small>
            Ohne Typ, Gesellschaft und Fachgruppe haben sie keine Kapazität. Wer schon gepflegt ist,
            wird mit seinem Stammsatz aus PACS zusammengeführt statt vervollständigt.
          </small>
        </p>
      )}
      <table>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Typ</th>
            <th scope="col">Gesellschaft</th>
            <th scope="col">Fachgruppe</th>
            <th scope="col">Rolle</th>
            <th scope="col">Eintritt</th>
            <th scope="col">Austritt</th>
            <th scope="col">Soll heute</th>
            <th scope="col">Status</th>
            <th scope="col">Aktion</th>
          </tr>
        </thead>
        <tbody>
          {mitarbeiter.map((employee) => (
            <tr key={employee.id} aria-current={employee.id === ausgewaehlt ? 'true' : undefined}>
              <td>{employee.name}</td>
              <td>{typAnzeige(employee)}</td>
              <td>{nameZu(gesellschaften, employee.companyId)}</td>
              <td>
                {nameZu(fachgruppen, employee.fachgruppeId)}
                {wechselHinweis(naechsterFachgruppenwechsel(employee, heute), fachgruppen)}
              </td>
              <td>{employee.roleId === null ? '—' : (rollen.get(employee.roleId) ?? '—')}</td>
              <td>{datumAnzeige(employee.startsOn)}</td>
              <td>{datumAnzeige(employee.endsOn)}</td>
              <td>{stundenAnzeige(sollHeute(employee, heute))}</td>
              <td>{statusAnzeige(employee, heute)}</td>
              <td>
                <button
                  type="button"
                  disabled={gesperrt}
                  aria-label={`${employee.name} ${istZuVervollstaendigen(employee) ? 'vervollständigen' : 'bearbeiten'}`}
                  onClick={() => {
                    onAuswahl(employee.id);
                  }}
                >
                  {istZuVervollstaendigen(employee) ? 'Vervollständigen' : 'Bearbeiten'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

export interface MitarbeiterFormularProps {
  /**
   * `vervollstaendigen` (AC-103, Ergaenzung): ein Stammsatz aus PACS oder eine
   * externe Person — Typ waehlen, bei „intern" die Pflichtangaben wie beim
   * Anlegen.
   */
  readonly modus: 'neu' | 'bearbeiten' | 'vervollstaendigen';
  readonly stand: FormularStand;
  readonly optionen: EmployeeOptions;
  /** Fruehester Tag fuer eine Aenderung der Soll-Wochenstunden (vom Server). */
  readonly changesFrom: IsoDate;
  /** Beim Bearbeiten: die heute zugeordnete Fachgruppe (vom Server, AC-104). */
  readonly aktuelleFachgruppeId?: string | null;
  /** Beim Bearbeiten: die heute zugeordnete Gesellschaft (vom Server, AC-103-Ergaenzung). */
  readonly aktuelleGesellschaftId?: string | null;
  /** Haengt ein PACS-Kontakt am Satz, kommt der Name aus PACS und ist hier nicht aenderbar. */
  readonly nameAusPacs?: boolean;
  readonly fehler: Partial<Record<FormularFeld, string>>;
  /**
   * Hinweise ohne Fehler — etwa „inzwischen anders gespeichert". Sie beschreiben
   * das Feld (`aria-describedby`), machen es aber nicht ungueltig.
   */
  readonly hinweise: Partial<Record<FormularFeld, string>>;
  /** Ein Schreibzugriff laeuft — beschriftet den Knopf. */
  readonly sendet: boolean;
  /** Eingaben und Abbrechen sind gesperrt: waehrend gespeichert oder geladen wird. */
  readonly gesperrt: boolean;
  /**
   * Speichern ist gesperrt, auch wenn nichts laeuft: nach einer Ablehnung (403)
   * und nach einem Anlegen mit ungewissem Ausgang — beides bis zum naechsten
   * erfolgreichen Laden — und waehrend des Ladens.
   */
  readonly speichernGesperrt: boolean;
  readonly onAendern: (feld: FormularFeld, wert: string) => void;
  readonly onSpeichern: () => void;
  readonly onAbbrechen: () => void;
}

/** Ein beschriftetes Feld mit Meldung und Hinweis, verknuepft ueber `aria-describedby`. */
function Feld({
  feld,
  label,
  fehler,
  hinweis,
  children,
}: {
  readonly feld: FormularFeld;
  readonly label: string;
  readonly fehler: string | undefined;
  readonly hinweis: string | undefined;
  readonly children: JSX.Element;
}): JSX.Element {
  return (
    <p>
      <label htmlFor={`mitarbeiter-${feld}`}>{label}</label> {children}
      {fehler === undefined ? null : <span id={`mitarbeiter-${feld}-fehler`}> {fehler}</span>}
      {hinweis === undefined ? null : <span id={`mitarbeiter-${feld}-hinweis`}> {hinweis}</span>}
    </p>
  );
}

export function MitarbeiterFormular({
  modus,
  stand,
  optionen,
  changesFrom,
  aktuelleFachgruppeId,
  aktuelleGesellschaftId,
  nameAusPacs = false,
  fehler,
  hinweise,
  sendet,
  gesperrt,
  speichernGesperrt,
  onAendern,
  onSpeichern,
  onAbbrechen,
}: MitarbeiterFormularProps): JSX.Element {
  const fachgruppenNamen = namenVon(optionen.fachgruppen);
  const gesellschaftsNamen = namenVon(optionen.companies);
  // Beim Vervollstaendigen als „intern" dieselben Pflichtangaben wie beim Anlegen.
  const ersteAngaben =
    modus === 'neu' || (modus === 'vervollstaendigen' && stand.personType === 'intern');
  const eingabe = (feld: FormularFeld) => {
    const beschreibung = [
      ...(fehler[feld] === undefined ? [] : [`mitarbeiter-${feld}-fehler`]),
      ...(hinweise[feld] === undefined ? [] : [`mitarbeiter-${feld}-hinweis`]),
    ].join(' ');

    return {
      id: `mitarbeiter-${feld}`,
      value: stand[feld],
      disabled: gesperrt,
      'aria-invalid': fehler[feld] !== undefined,
      ...(beschreibung === '' ? {} : { 'aria-describedby': beschreibung }),
      onChange: (event: { target: { value: string } }) => {
        onAendern(feld, event.target.value);
      },
    };
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSpeichern();
      }}
    >
      <h3>
        {modus === 'neu'
          ? 'Mitarbeiter anlegen'
          : modus === 'vervollstaendigen'
            ? 'Stammsatz vervollständigen'
            : 'Mitarbeiter bearbeiten'}
      </h3>

      <Feld feld="name" label="Name" fehler={fehler.name} hinweis={hinweise.name}>
        <input
          type="text"
          required
          maxLength={200}
          {...eingabe('name')}
          disabled={gesperrt || nameAusPacs || modus === 'vervollstaendigen'}
        />
      </Feld>
      {nameAusPacs ? (
        <p>
          <small>Der Name kommt aus PACS und wird dort gepflegt.</small>
        </p>
      ) : null}

      {modus === 'vervollstaendigen' ? (
        <>
          <Feld
            feld="personType"
            label="Typ"
            fehler={fehler.personType}
            hinweis={hinweise.personType}
          >
            <select required {...eingabe('personType')}>
              <option value="intern">Intern – mit Gesellschaft, Fachgruppe und Kapazität</option>
              <option value="extern">Extern – Dienstleister, ohne Kapazität</option>
            </select>
          </Feld>
          {stand.personType === 'extern' ? (
            <p>
              <small>
                Externe Personen buchen in PACS, gehören aber zu keiner Gesellschaft und keiner
                Fachgruppe und werden nicht eingeplant.
              </small>
            </p>
          ) : null}
        </>
      ) : null}

      {ersteAngaben ? (
        <Feld
          feld="companyId"
          label="Gesellschaft"
          fehler={fehler.companyId}
          hinweis={hinweise.companyId}
        >
          <select required {...eingabe('companyId')}>
            <option value="">— bitte wählen —</option>
            {optionen.companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </Feld>
      ) : modus === 'bearbeiten' ? (
        <p>
          Gesellschaft heute: <strong>{nameZu(gesellschaftsNamen, aktuelleGesellschaftId)}</strong>
        </p>
      ) : null}

      {modus === 'vervollstaendigen' && !ersteAngaben ? null : ersteAngaben ? (
        <>
          {/* Pflicht (AK-2 von AC-104): `required` haelt das Formular schon im
              Browser auf; entscheiden tut der Server. */}
          <Feld
            feld="fachgruppeId"
            label="Fachgruppe"
            fehler={fehler.fachgruppeId}
            hinweis={hinweise.fachgruppeId}
          >
            <select required {...eingabe('fachgruppeId')}>
              <option value="">— bitte wählen —</option>
              {optionen.fachgruppen.map((fachgruppe) => (
                <option key={fachgruppe.id} value={fachgruppe.id}>
                  {fachgruppe.name}
                </option>
              ))}
            </select>
          </Feld>
          <p>
            <small>Gesellschaft und Fachgruppe gelten ab dem Eintritt.</small>
          </p>
        </>
      ) : (
        <p>
          Fachgruppe heute: <strong>{nameZu(fachgruppenNamen, aktuelleFachgruppeId)}</strong>
        </p>
      )}

      {modus === 'vervollstaendigen' && !ersteAngaben ? null : (
        <>
          <Feld feld="roleId" label="Rolle" fehler={fehler.roleId} hinweis={hinweise.roleId}>
            <select {...eingabe('roleId')}>
              <option value="">— keine —</option>
              {optionen.roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </Feld>

          {/* Pflicht nur beim Anlegen: Altbestand ohne Eintritt bleibt so speicherbar. */}
          <Feld
            feld="startsOn"
            label="Eintritt"
            fehler={fehler.startsOn}
            hinweis={hinweise.startsOn}
          >
            <input type="date" required={ersteAngaben} {...eingabe('startsOn')} />
          </Feld>

          <Feld
            feld="endsOn"
            label="Austritt (letzter Arbeitstag)"
            fehler={fehler.endsOn}
            hinweis={hinweise.endsOn}
          >
            <input type="date" {...eingabe('endsOn')} />
          </Feld>
          {modus === 'bearbeiten' ? (
            <p>
              <small>
                Ein- und Austritt lassen sich nur mit Wirkung ab {datumAnzeige(changesFrom)} ändern;
                vergangene Kalenderwochen bleiben unverändert.
              </small>
            </p>
          ) : null}

          {modus === 'bearbeiten' ? (
            <fieldset>
              <legend>Gesellschaft wechseln (leer: unverändert)</legend>
              <Feld
                feld="companyId"
                label="Neue Gesellschaft"
                fehler={fehler.companyId}
                hinweis={hinweise.companyId}
              >
                <select {...eingabe('companyId')}>
                  <option value="">— unverändert —</option>
                  {optionen.companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </Feld>
              <Feld
                feld="companyAb"
                label="Gültig ab (Montag)"
                fehler={fehler.companyAb}
                hinweis={hinweise.companyAb}
              >
                <input type="date" {...eingabe('companyAb')} />
              </Feld>
              <p>
                <small>
                  Wie bei der Fachgruppe: ein Wechsel beginnt an einem Montag, frühestens am{' '}
                  {datumAnzeige(changesFrom)}; vergangene Kalenderwochen gehören der bisherigen
                  Gesellschaft.
                </small>
              </p>
            </fieldset>
          ) : null}

          {modus === 'bearbeiten' ? (
            <fieldset>
              <legend>Fachgruppe wechseln (leer: unverändert)</legend>
              <Feld
                feld="fachgruppeId"
                label="Neue Fachgruppe"
                fehler={fehler.fachgruppeId}
                hinweis={hinweise.fachgruppeId}
              >
                <select {...eingabe('fachgruppeId')}>
                  <option value="">— unverändert —</option>
                  {optionen.fachgruppen.map((fachgruppe) => (
                    <option key={fachgruppe.id} value={fachgruppe.id}>
                      {fachgruppe.name}
                    </option>
                  ))}
                </select>
              </Feld>
              {/* Kein `min` und kein `step`: ob ein Tag zulaessig ist (Montag, nicht
              vor der laufenden Woche), entscheidet der Server am Feld. */}
              <Feld
                feld="fachgruppeAb"
                label="Gültig ab (Montag)"
                fehler={fehler.fachgruppeAb}
                hinweis={hinweise.fachgruppeAb}
              >
                <input type="date" {...eingabe('fachgruppeAb')} />
              </Feld>
              <p>
                <small>
                  Ein Wechsel beginnt an einem Montag, frühestens am {datumAnzeige(changesFrom)}.
                  Bisherige Buchungen und vergangene Kalenderwochen bleiben unverändert; die
                  bisherige Zuordnung bleibt in der Historie erhalten.
                </small>
              </p>
            </fieldset>
          ) : null}

          <fieldset>
            <legend>
              {ersteAngaben
                ? 'Soll-Wochenstunden'
                : 'Soll-Wochenstunden ändern (leer: unverändert)'}
            </legend>
            <Feld
              feld="weeklyHours"
              label="Stunden je Woche"
              fehler={fehler.weeklyHours}
              hinweis={hinweise.weeklyHours}
            >
              <input
                type="text"
                inputMode="decimal"
                placeholder="z. B. 38,5"
                required={ersteAngaben}
                {...eingabe('weeklyHours')}
              />
            </Feld>
            {/* Kein `min`: ob ein Datum zulaessig ist, entscheidet der Server — ein
            Eintrag vor dem ersten fuellt auch rueckwirkend eine Luecke. */}
            {modus === 'bearbeiten' ? (
              <>
                <Feld
                  feld="effectiveFrom"
                  label="Gültig ab"
                  fehler={fehler.effectiveFrom}
                  hinweis={hinweise.effectiveFrom}
                >
                  <input type="date" {...eingabe('effectiveFrom')} />
                </Feld>
                <p>
                  <small>
                    Eine Änderung wirkt frühestens ab {datumAnzeige(changesFrom)}. Bereits
                    vergangene Kalenderwochen bleiben unverändert.
                  </small>
                </p>
              </>
            ) : (
              <p>
                <small>Gilt ab dem Eintritt.</small>
              </p>
            )}
          </fieldset>
        </>
      )}

      <p>
        <button type="submit" disabled={sendet || speichernGesperrt}>
          {sendet ? 'Wird gespeichert …' : 'Speichern'}
        </button>{' '}
        <button type="button" disabled={gesperrt} onClick={onAbbrechen}>
          Abbrechen
        </button>
      </p>
    </form>
  );
}

export interface SollHistorieProps {
  readonly eintraege: EmployeeDetails['weeklyHours'];
  readonly changesFrom: IsoDate;
  /** Waehrend gespeichert oder geladen wird und nach einer Sperre wird nichts entfernt. */
  readonly gesperrt: boolean;
  readonly onEntfernen: (effectiveFrom: IsoDate) => void;
}

/**
 * Die Soll-Historie. Ein Eintrag vor `changesFrom` liegt in einer
 * abgeschlossenen Woche und ist fest (AK-3); ein Eintrag ab `changesFrom` ist
 * aenderbar — auch einer ab dem Montag der laufenden Woche, der schon wirkt —
 * und laesst sich entfernen, ausser er ist der erste (`istEntfernbar`).
 */
export function SollHistorie({
  eintraege,
  changesFrom,
  gesperrt,
  onEntfernen,
}: SollHistorieProps): JSX.Element {
  return (
    <table>
      <caption>Soll-Wochenstunden</caption>
      <thead>
        <tr>
          <th scope="col">Gültig ab</th>
          <th scope="col">Stunden je Woche</th>
          <th scope="col">Stand</th>
        </tr>
      </thead>
      <tbody>
        {eintraege.map((eintrag) => {
          const aenderbar = istAenderbar(eintrag.effectiveFrom, changesFrom);
          const entfernbar = istEntfernbar(eintraege, eintrag.effectiveFrom, changesFrom);

          return (
            <tr key={eintrag.id}>
              <td>{datumAnzeige(eintrag.effectiveFrom)}</td>
              <td>{stundenAnzeige(eintrag.hours)}</td>
              <td>
                {aenderbar ? 'änderbar' : 'fest'}
                {entfernbar ? (
                  <>
                    {' '}
                    <button
                      type="button"
                      disabled={gesperrt}
                      aria-label={`Eintrag ab ${datumAnzeige(eintrag.effectiveFrom)} entfernen`}
                      onClick={() => {
                        onEntfernen(eintrag.effectiveFrom);
                      }}
                    >
                      Entfernen
                    </button>
                  </>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export interface FachgruppenHistorieProps {
  readonly eintraege: EmployeeDetails['fachgruppen'];
  readonly optionen: EmployeeOptions;
  readonly changesFrom: IsoDate;
  readonly sendet: boolean;
  readonly onEntfernen: (effectiveFrom: IsoDate) => void;
}

/** Eine Zuordnungs-Historie — Fachgruppe (AC-104) oder Gesellschaft (AC-103, Ergaenzung). */
function ZuordnungsHistorie({
  titel,
  spalte,
  eintraege,
  namen,
  changesFrom,
  sendet,
  onEntfernen,
}: {
  readonly titel: string;
  readonly spalte: string;
  readonly eintraege: readonly { id: string; effectiveFrom: IsoDate; wertId: string }[];
  readonly namen: ReadonlyMap<string, string>;
  readonly changesFrom: IsoDate;
  readonly sendet: boolean;
  readonly onEntfernen: (effectiveFrom: IsoDate) => void;
}): JSX.Element {
  return (
    <table>
      <caption>{titel}</caption>
      <thead>
        <tr>
          <th scope="col">Gültig ab</th>
          <th scope="col">{spalte}</th>
          <th scope="col">Stand</th>
        </tr>
      </thead>
      <tbody>
        {eintraege.map((eintrag, index) => {
          const entfernbar = istEntfernbar(eintraege, eintrag.effectiveFrom, changesFrom);

          return (
            <tr key={eintrag.id}>
              {/* Die erste Zuordnung gilt ab dem Eintritt; Altbestand ohne
                  Eintritt traegt die Untergrenze des Planungsfensters. */}
              <td>
                {index === 0
                  ? `ab Eintritt (${datumAnzeige(eintrag.effectiveFrom)})`
                  : datumAnzeige(eintrag.effectiveFrom)}
              </td>
              <td>{namen.get(eintrag.wertId) ?? '—'}</td>
              <td>
                {istAenderbar(eintrag.effectiveFrom, changesFrom) ? 'geplant' : 'fest'}
                {entfernbar ? (
                  <>
                    {' '}
                    <button
                      type="button"
                      disabled={sendet}
                      aria-label={`Wechsel ab ${datumAnzeige(eintrag.effectiveFrom)} entfernen`}
                      onClick={() => {
                        onEntfernen(eintrag.effectiveFrom);
                      }}
                    >
                      Entfernen
                    </button>
                  </>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * Die Fachgruppen-Zuordnung als Historie (AC-104, AK-3): seit wann die Person
 * zu welcher Fachgruppe gehoert. Eine Zuordnung vor `changesFrom` ist fest —
 * die abgeschlossene Woche gehoert ihrer damaligen Fachgruppe —, ein geplanter
 * Wechsel laesst sich entfernen, die erste Zuordnung nicht (`istEntfernbar`).
 */
export function FachgruppenHistorie({
  eintraege,
  optionen,
  changesFrom,
  sendet,
  onEntfernen,
}: FachgruppenHistorieProps): JSX.Element {
  return (
    <ZuordnungsHistorie
      titel="Fachgruppen-Zuordnung"
      spalte="Fachgruppe"
      eintraege={eintraege.map(({ id, effectiveFrom, fachgruppeId }) => ({
        id,
        effectiveFrom,
        wertId: fachgruppeId,
      }))}
      namen={namenVon(optionen.fachgruppen)}
      changesFrom={changesFrom}
      sendet={sendet}
      onEntfernen={onEntfernen}
    />
  );
}

export interface GesellschaftsHistorieProps {
  readonly eintraege: EmployeeDetails['companies'];
  readonly optionen: EmployeeOptions;
  readonly changesFrom: IsoDate;
  readonly sendet: boolean;
  readonly onEntfernen: (effectiveFrom: IsoDate) => void;
}

/**
 * Die Gesellschafts-Zuordnung als Historie (AC-103, Ergaenzung) — dieselbe
 * Tabelle wie die Fachgruppe, dieselben Regeln.
 */
export function GesellschaftsHistorie({
  eintraege,
  optionen,
  changesFrom,
  sendet,
  onEntfernen,
}: GesellschaftsHistorieProps): JSX.Element {
  return (
    <ZuordnungsHistorie
      titel="Gesellschafts-Zuordnung"
      spalte="Gesellschaft"
      eintraege={eintraege.map(({ id, effectiveFrom, companyId }) => ({
        id,
        effectiveFrom,
        wertId: companyId,
      }))}
      namen={namenVon(optionen.companies)}
      changesFrom={changesFrom}
      sendet={sendet}
      onEntfernen={onEntfernen}
    />
  );
}

export interface PacsZusammenfuehrenProps {
  /** Die Stammsaetze aus PACS, die noch zu vervollstaendigen sind. */
  readonly kandidaten: readonly {
    readonly id: string;
    readonly name: string;
    readonly pacsId: number;
  }[];
  /** Die Auswahl (Kennung des Stammsatzes aus PACS); `''` = keine. */
  readonly auswahl: string;
  readonly fehler: string | undefined;
  readonly gesperrt: boolean;
  readonly onAuswahl: (id: string) => void;
  readonly onZusammenfuehren: () => void;
}

/**
 * Einen gepflegten Satz mit seinem Stammsatz aus PACS zusammenfuehren (AC-103,
 * Ergaenzung): fuer wen der Abgleich einen zweiten Satz angelegt hat, weil er
 * in ASAS schon gepflegt war. Der Satz aus PACS faellt weg, der Name kommt
 * danach aus PACS.
 */
export function PacsZusammenfuehren({
  kandidaten,
  auswahl,
  fehler,
  gesperrt,
  onAuswahl,
  onZusammenfuehren,
}: PacsZusammenfuehrenProps): JSX.Element | null {
  if (kandidaten.length === 0) return null;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onZusammenfuehren();
      }}
    >
      <h3>Mit Stammsatz aus PACS zusammenführen</h3>
      <p>
        <label htmlFor="mitarbeiter-zusammenfuehren">Stammsatz aus PACS</label>{' '}
        <select
          id="mitarbeiter-zusammenfuehren"
          value={auswahl}
          disabled={gesperrt}
          aria-invalid={fehler !== undefined}
          {...(fehler === undefined
            ? {}
            : { 'aria-describedby': 'mitarbeiter-zusammenfuehren-fehler' })}
          onChange={(event) => {
            onAuswahl(event.target.value);
          }}
        >
          <option value="">— bitte wählen —</option>
          {kandidaten.map((kandidat) => (
            <option key={kandidat.id} value={kandidat.id}>
              {kandidat.name} (PACS {String(kandidat.pacsId)})
            </option>
          ))}
        </select>
        {fehler === undefined ? null : (
          <span id="mitarbeiter-zusammenfuehren-fehler"> {fehler}</span>
        )}
      </p>
      <p>
        <small>
          Der Stammsatz aus PACS wird entfernt; diese Person übernimmt seinen PACS-Kontakt und den
          Namen aus PACS. Gesellschaft, Fachgruppe und Soll bleiben, wie sie sind.
        </small>
      </p>
      <p>
        <button type="submit" disabled={gesperrt || auswahl === ''}>
          Zusammenführen
        </button>
      </p>
    </form>
  );
}

export const WOCHENSTATUS_LABELS: Readonly<Record<WeekCapacityStatus, string>> = {
  vor_eintritt: 'Vor Eintritt',
  beschaeftigt: 'Beschäftigt',
  teilweise: 'Teilweise (Ein- oder Austrittswoche)',
  nach_austritt: 'Nach Austritt',
  nicht_planbar: 'Nicht planbar (extern oder zu vervollständigen)',
};

/** Die Kapazitaet je Kalenderwoche, wie der Server sie rechnet (AK-1 bis AK-3). */
export function KapazitaetsVorschau({
  wochen,
}: {
  readonly wochen: readonly WeekCapacity[];
}): JSX.Element {
  return (
    <table>
      <caption>Kapazität je Kalenderwoche</caption>
      <thead>
        <tr>
          <th scope="col">KW</th>
          <th scope="col">ab</th>
          <th scope="col">Status</th>
          <th scope="col">Planbar</th>
          <th scope="col">Kapazität</th>
        </tr>
      </thead>
      <tbody>
        {wochen.map((woche) => (
          <tr key={woche.weekStart}>
            <td>
              KW {String(woche.isoWeek)}/{String(woche.isoYear)}
            </td>
            <td>{datumAnzeige(woche.weekStart)}</td>
            <td>
              {WOCHENSTATUS_LABELS[woche.status]}
              {woche.missingWeeklyHours ? ' – ohne Soll-Angabe' : ''}
            </td>
            <td>{woche.planbar ? 'ja' : 'nein'}</td>
            <td>{stundenAnzeige(woche.hours)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
